import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Checkbox } from "./ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"

import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
  SidebarFooter,
} from "./ui/sidebar"

import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Plus,
  Droplets,
  Sprout,
  Scissors,
  Shovel,
  Settings,
  CheckCircle2,
  Clock,
  User,
  LogOut,
  Menu,
  X,
  Sun,
  Cloud,
  CloudRain,
  Home,
  Heart,
  Leaf,
} from "lucide-react"
import { cn } from "../lib/utils"
import { useAuth } from "../contexts/AuthContext"

// Date utility functions to replace date-fns
const format = (date: Date, formatStr: string): string => {
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const hours = date.getHours()
  const minutes = date.getMinutes()
  
  return formatStr
    .replace('yyyy', year.toString())
    .replace('MM', month.toString().padStart(2, '0'))
    .replace('dd', day.toString().padStart(2, '0'))
    .replace('HH', hours.toString().padStart(2, '0'))
    .replace('mm', minutes.toString().padStart(2, '0'))
}

const startOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

const endOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

const startOfWeek = (date: Date): Date => {
  const day = date.getDay()
  const diff = date.getDate() - day
  return new Date(date.getFullYear(), date.getMonth(), diff)
}

const endOfWeek = (date: Date): Date => {
  const day = date.getDay()
  const diff = date.getDate() - day + 6
  return new Date(date.getFullYear(), date.getMonth(), diff)
}

const eachDayOfInterval = ({ start, end }: { start: Date; end: Date }): Date[] => {
  console.log('eachDayOfInterval called with start:', start, 'end:', end)
  const days: Date[] = []
  const current = new Date(start)
  let safetyCounter = 0
  const maxDays = 100 // Safety limit to prevent infinite loops
  
  // Fix: Use getTime() for proper date comparison
  const endTime = end.getTime()
  
  while (current.getTime() <= endTime && safetyCounter < maxDays) {
    days.push(new Date(current))
    current.setDate(current.getDate() + 1)
    safetyCounter++
  }
  
  if (safetyCounter >= maxDays) {
    console.error('Safety limit reached in eachDayOfInterval, stopping to prevent infinite loop')
  }
  
  console.log('eachDayOfInterval returning days:', days.length)
  return days
}

const isSameMonth = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth()
}

const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() && 
         date1.getMonth() === date2.getMonth() && 
         date1.getDate() === date2.getDate()
}

const isToday = (date: Date): boolean => {
  const today = new Date()
  return isSameDay(date, today)
}

const addMonths = (date: Date, months: number): Date => {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate())
}

const subMonths = (date: Date, months: number): Date => {
  return new Date(date.getFullYear(), date.getMonth() - months, date.getDate())
}

// Lithuanian month names
const ltMonths = [
  'Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis',
  'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'
]



// Sample calendar data
const calendarTasks = [
  {
    date: "2025-07-25",
    tasks: [
      { id: 1, title: "Palaistyti rožes", type: "watering", time: "09:00", completed: false, plant: "Rožės" },
      { id: 2, title: "Patikrinti pomidorus", type: "other", time: "14:00", completed: true, plant: "Pomidorai" }
    ]
  },
  {
    date: "2025-07-26",
    tasks: [
      { id: 3, title: "Patręšti žolynus", type: "fertilizing", time: "10:00", completed: false, plant: "Žolynas" }
    ]
  },
  {
    date: "2025-07-28",
    tasks: [
      { id: 4, title: "Nupjauti šaknis", type: "pruning", time: "11:00", completed: false, plant: "Obelis" },
      { id: 5, title: "Palaistyti kaktusus", type: "watering", time: "16:00", completed: false, plant: "Kaktusai" }
    ]
  },
  {
    date: "2025-07-30",
    tasks: [
      { id: 6, title: "Persodinti orkidelę", type: "repotting", time: "13:00", completed: false, plant: "Orkidelė" }
    ]
  },
  {
    date: "2025-08-02",
    tasks: [
      { id: 7, title: "Patręšti citrusus", type: "fertilizing", time: "08:00", completed: false, plant: "Citrusai" },
      { id: 8, title: "Palaistyti paparčius", type: "watering", time: "15:00", completed: false, plant: "Paparciai" },
      { id: 9, title: "Patikrinti sveikatą", type: "other", time: "17:00", completed: false, plant: "Visi augalai" }
    ]
  }
]

// Task type configurations
const taskTypes = {
  watering: { icon: Droplets, color: "bg-blue-500", label: "Laistymas" },
  fertilizing: { icon: Sprout, color: "bg-green-500", label: "Tręšimas" },
  pruning: { icon: Scissors, color: "bg-orange-500", label: "Genėjimas" },
  repotting: { icon: Shovel, color: "bg-purple-500", label: "Persodimas" },
  other: { icon: Settings, color: "bg-gray-500", label: "Kita" }
}

// Weather data (mock)
const weatherData: Record<string, { temp: number; condition: string; icon: any }> = {
  "2025-07-25": { temp: 18, condition: "sunny", icon: Sun },
  "2025-07-26": { temp: 15, condition: "cloudy", icon: Cloud },
  "2025-07-28": { temp: 12, condition: "rainy", icon: CloudRain },
  "2025-07-30": { temp: 20, condition: "sunny", icon: Sun },
  "2025-08-02": { temp: 16, condition: "cloudy", icon: Cloud }
}

export default function Calendar() {
  console.log('Calendar component rendering')
  
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month')
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [dayDetailOpen, setDayDetailOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Safety check to prevent infinite loops
  useEffect(() => {
    console.log('Calendar useEffect - currentDate check running')
    if (!currentDate || isNaN(currentDate.getTime())) {
      console.error('Invalid currentDate detected, resetting to today')
      setCurrentDate(new Date())
    }
  }, [currentDate])

  // Navigation items
  const navigationItems = [
    {
      title: "Pagrindinis",
      description: "Pagrindinis skydelis",
      url: '/dashboard',
      icon: Home
    },
    {
      title: "Mano Sodas",
      description: "Jūsų augalų sąrašas",
      url: '/my-plants',
      icon: Heart
    },
    {
      title: "Augalų Katalogas",
      description: "Augalų duomenų bazė",
      url: '/plants',
      icon: Leaf
    },
    {
      title: "Užduotys",
      description: "Priežiūros užduotys",
      url: '/tasks',
      icon: CheckCircle2
    },
    {
      title: "Kalendorius",
      description: "Priežiūros kalendorius",
      url: '/calendar',
      icon: CalendarIcon
    },
    {
      title: "Orai",
      description: "Oro prognozės",
      url: '/weather',
      icon: Cloud
    }
  ]



  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleNavigation = (url: string) => {
    navigate(url)
    setMobileSidebarOpen(false)
  }

  const getUserInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const isActiveRoute = (url: string) => {
    return window.location.pathname === url
  }

  // Calendar functions
  const goToPreviousMonth = useCallback(() => {
    setCurrentDate(subMonths(currentDate, 1))
  }, [currentDate])

  const goToNextMonth = useCallback(() => {
    setCurrentDate(addMonths(currentDate, 1))
  }, [currentDate])

  const goToToday = useCallback(() => {
    setCurrentDate(new Date())
    setSelectedDate(new Date())
  }, [])

  const getDaysInMonth = useMemo(() => {
    console.log('getDaysInMonth useMemo running, currentDate:', currentDate)
    try {
      const start = startOfWeek(startOfMonth(currentDate))
      const end = endOfWeek(endOfMonth(currentDate))
      const days = eachDayOfInterval({ start, end })
      console.log('getDaysInMonth calculated days:', days.length)
      return days
    } catch (error) {
      console.error('Error calculating days in month:', error)
      return []
    }
  }, [currentDate])

  const getTasksForDate = useCallback((date: Date) => {
    try {
      const dateString = format(date, 'yyyy-MM-dd')
      return calendarTasks.find(day => day.date === dateString)?.tasks || []
    } catch (error) {
      console.error('Error getting tasks for date:', error)
      return []
    }
  }, [])

  const getWeatherForDate = useCallback((date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd')
    return weatherData[dateString]
  }, [])

  const handleDateClick = useCallback((date: Date) => {
    try {
      setError(null)
      setSelectedDate(date)
      setDayDetailOpen(true)
    } catch (error) {
      console.error('Error handling date click:', error)
      setError('Klaida atidarant datos informaciją')
    }
  }, [])

  const handleTaskComplete = useCallback((taskId: number) => {
    // In a real app, this would update the task in the backend
    console.log('Task completed:', taskId)
  }, [])

  const getTaskTypeConfig = useCallback((type: string) => {
    return taskTypes[type as keyof typeof taskTypes] || taskTypes.other
  }, [])

  const TaskIndicator = React.memo(({ tasks }: { tasks: any[] }) => {
    if (tasks.length === 0) return null

    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {tasks.slice(0, 4).map((task, index) => {
          const config = getTaskTypeConfig(task.type)
          return (
            <div
              key={index}
              className={cn(
                "w-3 h-3 rounded-full",
                config.color,
                task.completed && "opacity-50"
              )}
              title={`${config.label}: ${task.title}`}
            />
          )
        })}
        {tasks.length > 4 && (
          <div className="w-3 h-3 rounded-full bg-gray-400 flex items-center justify-center">
            <span className="text-[8px] text-white font-bold">+{tasks.length - 4}</span>
          </div>
        )}
      </div>
    )
  })

  const DayDetailPanel = () => {
    if (!selectedDate) return null

    const tasks = getTasksForDate(selectedDate)
    const weather = getWeatherForDate(selectedDate)
    const WeatherIcon = weather?.icon || Sun

    return (
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="text-lg font-semibold">
                {ltMonths[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
              </span>
              {weather && (
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
                  <WeatherIcon className="w-4 h-4" />
                  <span>{weather.temp}°C</span>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.map((task) => {
                  const config = getTaskTypeConfig(task.type)
                  const Icon = config.icon
                  return (
                    <div
                      key={task.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border transition-all duration-200",
                        task.completed ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-sm"
                      )}
                    >
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => handleTaskComplete(task.id)}
                        className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                      />
                      <div className={cn("p-2 rounded-lg", config.color)}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("font-medium text-gray-900", task.completed && "line-through text-gray-500")}>
                          {task.title}
                        </p>
                        <p className="text-sm text-gray-600">{task.plant}</p>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500 bg-gray-50 px-2 py-1 rounded">
                        <Clock className="w-3 h-3" />
                        <span>{task.time}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Šiai dienai užduočių nėra</p>
              </div>
            )}
            
            <Button 
              onClick={() => {
                setDayDetailOpen(false)
                setShowCreateDialog(true)
              }}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Pridėti užduotį
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  const CalendarGrid = React.memo(() => {
    console.log('CalendarGrid rendering')
    const days = getDaysInMonth
    const weekDays = ['Pir', 'Ant', 'Tre', 'Ket', 'Pen', 'Šeš', 'Sek']

    return (
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Calendar Header */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 border-b">
          {weekDays.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-semibold text-gray-700 bg-white">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {days.map((day: Date, index: number) => {
            const tasks = getTasksForDate(day)
            const isCurrentMonth = isSameMonth(day, currentDate)
            const isCurrentDay = isToday(day)
            const isSelected = selectedDate && isSameDay(day, selectedDate)
            const dayKey = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}-${index}`

            return (
              <div
                key={dayKey}
                className={cn(
                  "min-h-[100px] p-2 bg-white cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:shadow-sm border border-transparent",
                  !isCurrentMonth && "text-gray-400 bg-gray-50",
                  isCurrentDay && "bg-emerald-50 border-2 border-emerald-500 shadow-sm",
                  isSelected && "bg-emerald-100 border-emerald-300"
                )}
                onClick={() => handleDateClick(day)}
              >
                <div className={cn(
                  "text-sm font-medium mb-1",
                  isCurrentDay && "text-emerald-700 font-semibold"
                )}>
                  {day.getDate()}
                </div>
                <TaskIndicator tasks={tasks} />
              </div>
            )
          })}
        </div>
      </div>
    )
  })

  return (
    <>
      <div className="flex h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
        {/* Desktop Sidebar */}
        <Sidebar className={`hidden lg:flex w-64 flex-col bg-white/80 backdrop-blur-sm border-r border-emerald-100 shadow-2xl transition-all duration-300`}>
          {/* Sidebar Header */}
          <SidebarHeader className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                <Leaf className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-emerald-800">Kiemo Draugas</span>
                <span className="text-xs text-emerald-600">Augalų valdymas</span>
              </div>
            </div>
          </SidebarHeader>
          
          {/* Navigation Content */}
          <SidebarContent className="flex-1 bg-white/80 backdrop-blur-sm px-3 py-4">
            <SidebarNav className="space-y-1">
              {navigationItems.map((item) => {
                const isActive = isActiveRoute(item.url)
                return (
                  <SidebarNavItem
                    key={item.title}
                    active={isActive}
                    as="div"
                    className={`
                      group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                      ${isActive 
                        ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                        : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                      }
                    `}
                    onClick={() => handleNavigation(item.url)}
                  >
                    <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                    </div>
                  </SidebarNavItem>
                )
              })}
            </SidebarNav>
          </SidebarContent>
          
          {/* Sidebar Footer */}
          <SidebarFooter className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
            <SidebarNav>
              <SidebarNavItem 
                as="div"
                className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
              >
                <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                <div className="flex flex-col">
                  <span>Nustatymai</span>
                  <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                </div>
              </SidebarNavItem>
            </SidebarNav>
          </SidebarFooter>
        </Sidebar>

        {/* Mobile Sidebar Overlay */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
            <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm shadow-2xl">
              <div className="flex h-full flex-col">
                {/* Mobile Header */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                        <Leaf className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-lg font-bold text-emerald-800">Kiemo Draugas</span>
                        <span className="text-xs text-emerald-600">Augalų valdymas</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-emerald-700 hover:bg-emerald-100"
                      onClick={() => setMobileSidebarOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Mobile Navigation */}
                <div className="flex-1 px-3 py-4">
                  <SidebarNav className="space-y-1">
                    {navigationItems.map((item) => {
                      const isActive = isActiveRoute(item.url)
                      return (
                        <SidebarNavItem
                          key={item.title}
                          active={isActive}
                          as="div"
                          className={`
                            group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                            ${isActive 
                              ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                              : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                            }
                          `}
                          onClick={() => handleNavigation(item.url)}
                        >
                          <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                          <div className="flex flex-col">
                            <span>{item.title}</span>
                            <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                          </div>
                        </SidebarNavItem>
                      )
                    })}
                  </SidebarNav>
                </div>

                {/* Mobile Footer */}
                <div className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
                  <SidebarNav>
                    <SidebarNavItem 
                      as="div"
                      className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
                    >
                      <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                      <div className="flex flex-col">
                        <span>Nustatymai</span>
                        <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                      </div>
                    </SidebarNavItem>
                  </SidebarNav>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Kalendorius</h1>
                  <p className="text-sm text-gray-500">Planuokite ir valdykite savo augalų priežiūros grafiką</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Sukurti užduotį
                </Button>

                                <div className="relative dropdown-container" ref={dropdownRef}>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full hover:bg-emerald-50"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <Avatar className="h-8 w-8 border-2 border-emerald-200">
                      <AvatarImage src="/placeholder.svg?height=32&width=32" alt="User avatar" />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700">
                        {user ? getUserInitials(user.full_name) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 user-dropdown py-1">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user ? user.full_name : 'Nežinomas vartotojas'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user ? user.email : ''}
                        </p>
                      </div>
                      
                      <div className="py-1">
                        <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 cursor-pointer">
                          <User className="w-4 h-4" />
                          <span>Profilis</span>
                        </div>
                        <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 cursor-pointer">
                          <Settings className="w-4 h-4" />
                          <span>Nustatymai</span>
                        </div>
                      </div>
                      
                      <div className="border-t border-gray-200 py-1">
                        <div 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                          onClick={() => {
                            setIsDropdownOpen(false)
                            handleLogout()
                          }}
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Atsijungti</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-auto p-6">
            {/* Calendar Controls */}
            <div className="mb-6 space-y-4">
              {/* Navigation and View Controls */}
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousMonth}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <h2 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
                    {ltMonths[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextMonth}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToToday}
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  >
                    Šiandien
                  </Button>
                  
                  <Select value={viewMode} onValueChange={(value: 'month' | 'week' | 'day') => setViewMode(value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">Mėnuo</SelectItem>
                      <SelectItem value="week">Savaitė</SelectItem>
                      <SelectItem value="day">Diena</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Task Type Legend */}
              <div className="flex flex-wrap gap-4 p-4 bg-white rounded-lg border">
                <h3 className="text-sm font-medium text-gray-700 w-full mb-2">Užduočių tipai:</h3>
                {Object.entries(taskTypes).map(([key, config]) => {
                  const Icon = config.icon
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <div className={cn("w-3 h-3 rounded-full", config.color)} />
                      <Icon className="w-3 h-3 text-gray-600" />
                      <span className="text-sm text-gray-600">{config.label}</span>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 text-red-600 border-red-300 hover:bg-red-100"
                  onClick={() => setError(null)}
                >
                  Uždaryti
                </Button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <span className="ml-2 text-emerald-700">Kraunama...</span>
              </div>
            )}

            {/* Calendar Grid */}
            {!isLoading && <CalendarGrid />}
          </main>
        </div>
      </div>

      {/* Day Detail Panel */}
      <DayDetailPanel />

      {/* Create Task Dialog (placeholder) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sukurti naują užduotį</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="taskTitle">Pavadinimas</Label>
              <Input id="taskTitle" placeholder="Įveskite užduoties pavadinimą" />
            </div>
            <div>
              <Label htmlFor="taskType">Tipas</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Pasirinkite užduoties tipą" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(taskTypes).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowCreateDialog(false)} variant="outline">
                Atšaukti
              </Button>
              <Button onClick={() => setShowCreateDialog(false)}>
                Sukurti
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
} 